import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Represents a file item in the upload queue, replacing ng2-file-upload's FileItem.
 */
export interface UploadFileItem {
  file: File;
  url?: string;
  method?: string;
  withCredentials?: boolean;
  alias?: string;
}

/**
 * Native Angular file upload service replacing ng2-file-upload.
 * Supports both PUT (signed URL uploads to S3) and POST (FormData) patterns.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadService {
  constructor(private http: HttpClient, private ngZone: NgZone) {}

  /**
   * Ensures observable emissions run inside NgZone so Angular change detection triggers.
   */
  private insideZone<T>(obs: Observable<T>): Observable<T> {
    return new Observable<T>(subscriber => {
      return obs.subscribe({
        next: (value) => this.ngZone.run(() => subscriber.next(value)),
        error: (err) => this.ngZone.run(() => subscriber.error(err)),
        complete: () => this.ngZone.run(() => subscriber.complete())
      });
    });
  }

  /**
   * Upload a file using POST with FormData (standard multipart upload).
   */
  upload(file: File, url: string, headers?: HttpHeaders): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.insideZone(this.http.request(new HttpRequest('POST', url, formData, {
      headers,
      reportProgress: true
    })));
  }

  /**
   * Upload a file using PUT with the raw file body (e.g., to a pre-signed S3 URL).
   */
  uploadRaw(file: File, url: string, method: string = 'PUT', headers?: HttpHeaders): Observable<HttpEvent<any>> {
    const effectiveHeaders = headers || new HttpHeaders({
      'Content-Type': file.type || 'application/octet-stream'
    });
    return this.insideZone(this.http.request(new HttpRequest(method, url, file, {
      headers: effectiveHeaders,
      reportProgress: true
    })));
  }
}
